import {
  ExportFeedbackCommandInput,
  ExportFeedbackCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { feedbackKeys } from 'src/data/api/helios-service-ts/feedback/queries/keys';
import { feedbackService } from 'src/data/api/helios-service-ts/feedback/service';

/**
 * React Query mutation hook for generating a business-unit-scoped feedback export.
 *
 * The Helios service generates (or reuses a recent) CSV export for the business unit and returns
 * a short-lived presigned S3 download URL along with generation/expiry metadata. The export is a
 * server-side artifact, so the browser only receives the download link — no feedback rows are
 * loaded into the page.
 *
 * @param businessUnitId - The business unit to scope the export to. Used as the mutation key.
 * @param options - Optional React Query mutation options. `mutationKey` and `mutationFn` are
 *                  handled internally and should not be provided.
 * @returns A React Query mutation object (mutate/mutateAsync plus status/data/error).
 *
 * @example
 * ```tsx
 * const { mutate, isPending } = useExportFeedbackMutation(businessUnitId, {
 *   onSuccess: (output) => window.open(output.downloadUrl, '_blank'),
 * });
 * mutate({ businessUnitId });
 * ```
 */
export const useExportFeedbackMutation = (
  businessUnitId: string,
  options?: Omit<
    UseMutationOptions<ExportFeedbackCommandOutput, Error, ExportFeedbackCommandInput>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation({
    ...options,
    mutationKey: feedbackKeys.export({ businessUnitId }),
    mutationFn: (data: ExportFeedbackCommandInput) => feedbackService.export(data),
  });
};
